import { CSSProperties, useEffect, useMemo, useState } from 'react'
import { PortalBranding, resolvePortalBranding } from '../../../pure-js/libs/PortalBrandingHelper'
import { useClientRouteHost } from '../context/ClientRouteHostContext'
import { readCachedPortalBranding, writeCachedPortalBranding } from '../libs/PortalBrandingCache'
import { useClientByClientId } from './useClient'
import { usePortalAuth } from './usePortalAuth'

/**
 * Branding for whichever portal the visitor is on. Resolves to Coursely defaults on the unified
 * portal (app.coursely.se) and on an unrecognised hostname.
 *
 * Resolution takes two sequential async hops — hostname → clientId, then clientId → client document
 * — so the first render has nothing to go on. Rather than block on that, the previous visit's
 * result is read synchronously from localStorage and rendered immediately, then replaced once the
 * real value lands. That makes the branding flash invisible on every visit except the first on a
 * given device, where the defaults still paint briefly (NFR-1).
 */
export const usePortalBranding = (): PortalBranding => {
  const { isViewAs, viewAsClientId } = usePortalAuth()
  const { clientId: hostClientId, clientHost, isLoading: hostLoading } = useClientRouteHost()
  const selectedViewAsClientId = isViewAs ? viewAsClientId : null
  const clientId = selectedViewAsClientId || hostClientId
  const isLoading = selectedViewAsClientId ? false : hostLoading
  const client = useClientByClientId(clientId)

  // Initializer form, not an effect: the seed has to exist before the first paint to be worth having.
  // View-as on a selected school must not reuse (or later overwrite) the hostname cache — localhost
  // and app.coursely.se would otherwise keep showing the last previewed school.
  const [cachedBranding] = useState(() => (selectedViewAsClientId ? null : readCachedPortalBranding(clientHost)))

  // The unified portal and unknown hostnames are settled as soon as the lookup stops; a client
  // portal is only settled once its client document has actually arrived.
  const isSettled = !!client || (!isLoading && !clientId)

  const branding = useMemo(() => {
    if (isSettled) return resolvePortalBranding(client)
    return cachedBranding ?? resolvePortalBranding(undefined)
  }, [isSettled, client, cachedBranding])

  useEffect(() => {
    if (!isSettled || selectedViewAsClientId) return
    writeCachedPortalBranding(clientHost, branding)
  }, [isSettled, clientHost, branding, selectedViewAsClientId])

  useApplyPortalBrandingToRoot(branding)

  return branding
}

const CSS_VAR_NAMES = [
  '--portal-primary',
  '--portal-primary-rgb',
  '--portal-primary-dark',
  '--portal-primary-light',
  '--portal-primary-surface',
  '--portal-primary-surface-strong'
] as const

const getPortalBrandingCssVarEntries = (branding: PortalBranding): [(typeof CSS_VAR_NAMES)[number], string][] => [
  ['--portal-primary', branding.primaryColor],
  ['--portal-primary-rgb', branding.primaryColorRgb],
  ['--portal-primary-dark', branding.primaryColorDark],
  ['--portal-primary-light', branding.primaryColorLight],
  ['--portal-primary-surface', branding.primaryColorSurface],
  ['--portal-primary-surface-strong', branding.primaryColorSurfaceStrong]
]

/**
 * Mirrors the branding variables onto `<html>`, on top of the inline style the portal containers
 * already carry. The containers cover the first paint (an effect runs too late for that); this
 * covers everything React renders *outside* the layout tree — MUI dialogs, popovers and toasts all
 * mount into document.body, where a variable scoped to the layout element would not reach them.
 */
const useApplyPortalBrandingToRoot = (branding: PortalBranding): void => {
  useEffect(() => {
    if (typeof document === 'undefined') return

    const root = document.documentElement
    for (const [name, value] of getPortalBrandingCssVarEntries(branding)) {
      root.style.setProperty(name, value)
    }

    return () => {
      for (const name of CSS_VAR_NAMES) {
        root.style.removeProperty(name)
      }
    }
  }, [branding])
}

/** CSS custom properties to spread onto a portal root element. */
export const getPortalBrandingCssVars = (branding: PortalBranding): CSSProperties =>
  Object.fromEntries(getPortalBrandingCssVarEntries(branding)) as CSSProperties
